import { useCallback } from 'react';
import { NormalFormSelectionField, NormalFormDateField } from '../molecules';
import { Button } from '../atoms';
import {
  DepositsReportFilterInput,
  DepositStatusOption,
  OrderDirectionOption,
  PageSizeOption,
} from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import {
  orderDirectionMapper,
  pageSizeMapper,
} from '@/helpers';

type DepositsFiltersProps = {
  filters: DepositsReportFilterInput;
  onFilterChange: (newFilters: DepositsReportFilterInput) => void;
};

const textsData = {
  en: {
    field1: 'Status',
    field2: 'Order',
    field3: 'Page Size',
    field4: 'Created From',
    field5: 'Created To',
    field6: 'Reference',
    clearFilters: 'Clear Filters',
  },
  es: {
    field1: 'Estado',
    field2: 'Orden',
    field3: 'Registros por página',
    field4: 'Fecha Desde',
    field5: 'Fecha Hasta',
    field6: 'Referencia',
    clearFilters: 'Limpiar Filtros',
  },
};

const depositStatusMapper = (status: DepositStatusOption, language: 'es' | 'en'): string => {
  const mapping = {
    en: {
      [DepositStatusOption.Pending]: 'Pending',
      [DepositStatusOption.Received]: 'Received',
      [DepositStatusOption.Returned]: 'Returned',
      [DepositStatusOption.Cancelled]: 'Cancelled',
    },
    es: {
      [DepositStatusOption.Pending]: 'Pendiente',
      [DepositStatusOption.Received]: 'Recibido',
      [DepositStatusOption.Returned]: 'Devuelto',
      [DepositStatusOption.Cancelled]: 'Cancelado',
    },
  };
  return mapping[language][status];
};

const initialFilters: DepositsReportFilterInput = {
  createdAtFrom: null,
  createdAtTo: null,
  statusOption: null,
  reference: null,
  orderDirection: OrderDirectionOption.Desc,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
};

export const DepositsFilters = ({ filters, onFilterChange }: DepositsFiltersProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const handleFieldChange = useCallback(
    (field: keyof DepositsReportFilterInput, value: string | null) => {
      const newFilters: DepositsReportFilterInput = {
        ...filters,
        [field]: value,
      };
      onFilterChange(newFilters);
    },
    [filters, onFilterChange]
  );

  const statusOptions = Object.values(DepositStatusOption).map((value) => ({
    name: depositStatusMapper(value, userLanguage),
    value: value,
  }));

  const orderDirectionOptions = Object.values(OrderDirectionOption).map((value) => ({
    name: orderDirectionMapper(value, userLanguage),
    value: value,
  }));

  const pageSizeOptions = [
    PageSizeOption.TwentyFive,
    PageSizeOption.Fifty,
    PageSizeOption.SeventyFive,
    PageSizeOption.OneHundred,
  ].map((value) => ({
    name: pageSizeMapper(value, userLanguage),
    value: value,
  }));

  const formatDateForInput = (date: string | null | undefined): string => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      return dateObj.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handleDateChange = (field: 'createdAtFrom' | 'createdAtTo', value: string | null) => {
    const newFilters: DepositsReportFilterInput = {
      ...filters,
      [field]: value ? new Date(value).toISOString() : null,
    };
    onFilterChange(newFilters);
  };

  return (
    <div className="bg-white p-6 rounded-lg mb-8">
      {/* First row: date fields + status + order */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-center">
        <div>
          <NormalFormDateField
            label={texts.field4}
            name="createdAtFrom"
            value={formatDateForInput(filters.createdAtFrom)}
            onChange={(value) => handleDateChange('createdAtFrom', value)}
          />
        </div>
        <div>
          <NormalFormDateField
            label={texts.field5}
            name="createdAtTo"
            value={formatDateForInput(filters.createdAtTo)}
            onChange={(value) => handleDateChange('createdAtTo', value)}
          />
        </div>
        <div>
          <NormalFormSelectionField<DepositStatusOption>
            label={texts.field1}
            name="statusOption"
            options={statusOptions}
            value={filters.statusOption}
            onChange={(value) => handleFieldChange('statusOption', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </div>
        <div>
          <NormalFormSelectionField<OrderDirectionOption>
            label={texts.field2}
            name="orderDirection"
            options={orderDirectionOptions}
            value={filters.orderDirection}
            onChange={(value) => handleFieldChange('orderDirection', value)}
          />
        </div>
      </div>

      {/* Second row: page size + clear button */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-center mt-4">
        <div>
          <NormalFormSelectionField<PageSizeOption>
            label={texts.field3}
            name="pageSize"
            options={pageSizeOptions}
            value={filters.pageSize}
            onChange={(value) => handleFieldChange('pageSize', value)}
            noValueText={userLanguage === 'es' ? 'Todos' : 'All'}
          />
        </div>
        <div className="sm:col-span-1 md:col-span-3 flex justify-end mt-4 md:mt-0">
          <Button
            secondary
            onClick={() => {
              onFilterChange(initialFilters);
            }}
          >
            {texts.clearFilters}
          </Button>
        </div>
      </div>
    </div>
  );
};
